export const translations = {
  en: {
    // Header
    nav: {
      features: "Features",
      trust: "Trust Us",
      waitlist: "Waitlist",
      pricing: "Pricing",
      caseStudies: "Case Studies",
      support: "Support",
      maintenance: "Maintenance",
      alerts: "Alerts",
      contact: "Contact",
      roiCalculator: "ROI Calculator"
    },
    // Hero Section
    hero: {
      badge: "Enterprise Fleet Management Solution",
      title: "Smart Fleet Management for Modern Business",
      subtitle: "Real-time tracking, maintenance optimization, and fleet analytics for businesses of all sizes",
      cta: {
        demo: "Request Demo",
        plans: "View Plans"
      },
      stats: {
        uptime: {
          value: "99.9%",
          label: "Platform Uptime"
        },
        clients: {
          value: "500+",
          label: "Active Clients"
        },
        vehicles: {
          value: "5,000+",
          label: "Vehicles Tracked"
        }
      },
      waitlistPlaceholder: "Enter your email",
      waitlistSubmit: "Join Waitlist",
      waitlistSubmitting: "Joining...",
      waitlistSuccess: "Great! You're on the list. We'll be in touch soon.",
      waitlistError: "Oops! Something went wrong. Please try again."
    },
    // Features Section
    features: {
      title: "Everything You Need to Manage Your Fleet",
      subtitle: "Comprehensive tools designed for fleet managers and business owners",
      items: [
        {
          title: "Real-time Analytics",
          description: "Monitor your fleet's performance with detailed analytics and customizable dashboards."
        },
        {
          title: "Smart Maintenance",
          description: "Predictive maintenance alerts and scheduling to prevent breakdowns and optimize vehicle health."
        },
        {
          title: "Enhanced Security",
          description: "Advanced security features including geofencing and real-time alerts."
        },
        {
          title: "Route Optimization",
          description: "AI-powered route planning to reduce fuel consumption and improve efficiency."
        },
        {
          title: "Driver Management",
          description: "Track driver behavior and performance with detailed scorecards."
        },
        {
          title: "Automated Reports",
          description: "Generate comprehensive reports automatically for better decision making."
        }
      ]
    },
    // Trust Section
    trust: {
      title: "Trusted by Growing Companies",
      subtitle: "Join the businesses that trust Drivi for their fleet management needs",
      partners: {
        cityExpress: {
          name: "Local Delivery Co.",
          type: "Local Delivery",
          vehicles: "12 vehicles",
          testimonial: "Drivi helped us optimize our delivery routes and reduce fuel costs by 15%. The real-time tracking is invaluable for our operations."
        },
        greenLogistics: {
          name: "Eco Transport Solutions",
          type: "Regional Transport",
          vehicles: "8 vehicles",
          testimonial: "As a growing transport company, we needed a scalable solution. Drivi provided exactly that, with excellent support."
        },
        quickCourier: {
          name: "Swift Courier Services",
          type: "Local Courier",
          vehicles: "15 vehicles",
          testimonial: "The real-time tracking and delivery management features have transformed our operations."
        },
        taxiFleet: {
          name: "City Ride Co.",
          type: "Ride Service",
          vehicles: "20 vehicles",
          testimonial: "Managing our fleet has never been easier. The dispatch and driver coordination features are exceptional."
        }
      },
      metrics: {
        activeFleets: {
          value: "25+",
          label: "Active Fleets"
        },
        vehiclesTracked: {
          value: "200+",
          label: "Vehicles Tracked"
        },
        satisfaction: {
          value: "95%",
          label: "Customer Satisfaction"
        },
        costReduction: {
          value: "25%",
          label: "Average Cost Reduction"
        }
      }
    },
    // Mobile App Section
    mobileApp: {
      badge: "Mobile Experience",
      title: "Control Your Fleet From Anywhere",
      subtitle: "Access real-time fleet data, manage operations, and respond to alerts instantly with our powerful mobile application.",
      features: {
        crossPlatform: {
          title: "Cross-Platform",
          description: "Available for iOS and Android devices"
        },
        secureAccess: {
          title: "Secure Access",
          description: "Biometric authentication support"
        },
        realTime: {
          title: "Real-Time Updates",
          description: "Instant notifications and alerts"
        },
        fullControl: {
          title: "Full Control",
          description: "Manage all fleet operations remotely"
        }
      },
      cta: {
        ios: "Download for iOS",
        android: "Download for Android"
      }
    },
    // Case Studies Section
    caseStudies: {
      title: "Success Stories",
      subtitle: "See how Spanish companies transformed their fleet operations",
      cases: [
        {
          company: "Transportes García e Hijos",
          type: "Logistics",
          description: "A family business that reduced fuel costs by 30% and improved delivery times by 25% using our route optimization."
        },
        {
          company: "Ambulancias Tenorio",
          type: "Healthcare",
          description: "Achieved 99.9% fleet utilization and reduced response times by 45% in the Extremadura region."
        },
        {
          company: "Taxis Unidos Valencia",
          type: "Transport",
          description: "Improved customer satisfaction by 40% and reduced operational costs by 35% across their 200-vehicle fleet."
        }
      ],
      readMore: "Read full story"
    },
    // Support Section
    support: {
      title: "Get in Touch",
      subtitle: "Have questions? We're here to help.",
      status: "All systems operational",
      cards: {
        phone: {
          title: "Phone Support",
          description: "Get immediate assistance from our support team",
          contact: "+34 676 984 400"
        },
        technical: {
          title: "Technical Support",
          description: "Expert technical assistance for your implementation",
          response: "< 2 hour response time"
        },
        account: {
          title: "Account Management",
          description: "Dedicated account manager for enterprise clients",
          availability: "Available 24/7"
        }
      },
      form: {
        title: "Contact Us",
        subtitle: "Fill out the form below and we'll get back to you as soon as possible.",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Work Email",
        company: "Company Name",
        message: "Message",
        submit: "Send Message",
        sending: "Sending...",
        successMessage: "Message sent successfully! We'll get back to you soon.",
        errorMessage: "Error sending message. Please try again.",
        firstNamePlaceholder: "Enter your first name",
        lastNamePlaceholder: "Enter your last name",
        emailPlaceholder: "Enter your work email",
        companyPlaceholder: "Enter your company name",
        messagePlaceholder: "How can we help you?"
      }
    },
    // ROI Section
    roi: {
      title: "ROI Calculator",
      subtitle: "Calculate your potential savings with Drivi",
      form: {
        fleetSize: "Fleet Size",
        mileage: "Average Monthly Mileage per Vehicle",
        fuelCost: "Fuel Cost",
        maintenanceCost: "Monthly Maintenance Cost per Vehicle"
      },
      results: {
        title: "Your Potential Annual Savings",
        fuelSavings: {
          label: "Fuel Savings"
        },
        maintenanceSavings: {
          label: "Maintenance Savings"
        },
        efficiencySavings: {
          label: "Efficiency Gains"
        },
        totalSavings: {
          label: "Total Annual Savings"
        }
      },
      cta: {
        calculate: "Download Full Report"
      }
    },
    // CTA Section
    cta: {
      title: "Ready to Transform Your Fleet Operations?",
      subtitle: "Join industry leaders who trust Drivi for their fleet management needs",
      demo: "Schedule Enterprise Demo",
      contact: "Contact Sales"
    },
    // Footer
    footer: {
      tagline: "Transform your fleet management with Drivi",
      sections: {
        product: {
          title: "Product",
          links: ["Features", "Pricing", "Updates", "Beta Program"]
        },
        company: {
          title: "Company",
          links: ["About", "Careers", "Blog", "Press"]
        },
        legal: {
          title: "Legal",
          links: ["Privacy", "Terms", "Security", "GDPR"]
        }
      },
      copyright: "© 2024 Drivi. All rights reserved."
    },
    // Fleet Status Section
    fleetStatus: {
      title: "Live Fleet Status",
      types: {
        delivery: "Delivery",
        transport: "Transport",
        emergency: "Emergency",
        service: "Service"
      },
      alerts: {
        stopped: "Vehicle stopped for delivery",
        delay: "Traffic delay reported",
        completed: "Delivery completed",
        deviation: "Route deviation detected"
      },
      total: "Total",
      active: "active",
      stopped: "stopped",
      utilization: "utilization",
      totalActive: "Total Active",
      fleetSize: "Fleet Size",
      recentAlerts: "Recent Alerts"
    },
    // Pricing Section
    pricing: {
      title: "Simple, transparent pricing",
      subtitle: "Choose the perfect plan for your fleet management needs",
      plans: {
        basic: {
          name: "Basic",
          price: "$Soon",
          period: "per month",
          description: "Perfect for small fleets and startups",
          features: [
            "Up to 10 vehicles",
            "Real-time tracking",
            "Basic analytics",
            "Email support",
            "Route optimization",
            "Advanced analytics",
            "API access",
            "Custom integrations"
          ],
          cta: "Start Basic"
        },
        professional: {
          name: "Professional",
          price: "$Soon",
          period: "per month",
          description: "Ideal for growing fleets",
          features: [
            "Up to 50 vehicles",
            "Real-time tracking",
            "Basic analytics",
            "Priority support",
            "Route optimization",
            "Advanced analytics",
            "API access",
            "Custom integrations"
          ],
          cta: "Start Professional",
          popular: "Most Popular"
        },
        enterprise: {
          name: "Enterprise",
          price: "Custom",
          period: "contact us",
          description: "For large fleets and special requirements",
          features: [
            "Unlimited vehicles",
            "Real-time tracking",
            "Basic analytics",
            "24/7 phone support",
            "Route optimization",
            "Advanced analytics",
            "API access",
            "Custom integrations"
          ],
          cta: "Contact Sales"
        }
      }
    },
    comingSoon: {
      backToHome: "Back to Home",
      title: "Coming Soon",
      description: "We're working hard to bring you the best fleet management platform.",
      waitlistTitle: "Join the Waitlist",
      waitlistDescription: "Be the first to know when we launch.",
      features: {
        speed: {
          title: "Lightning Fast",
          description: "Real-time updates and instant notifications"
        },
        security: {
          title: "Bank-Level Security",
          description: "Your data is protected with enterprise-grade encryption"
        },
        analytics: {
          title: "Smart Analytics",
          description: "Make data-driven decisions with advanced analytics"
        }
      },
      waitlist: {
        benefits: {
          earlyAccess: {
            title: "Early Access",
            description: "Be among the first to try our platform"
          },
          exclusiveUpdates: {
            title: "Exclusive Updates",
            description: "Get development updates and insider news"
          },
          specialOffers: {
            title: "Special Offers",
            description: "Access to launch-day discounts and promotions"
          }
        },
        subtitle: "Join the waitlist today"
      }
    },
    seo: {
      title: "Drivi - Smart Fleet Management & Vehicle Tracking Solutions",
      description: "Optimize your fleet management with Drivi. Real-time vehicle tracking, route optimization, and performance monitoring to boost efficiency and security.",
      keywords: "fleet tracking, vehicle tracking, fleet management, real-time tracking, GPS monitoring, vehicle security, fleet efficiency, route optimization",
      og: {
        title: "Drivi - Smart Fleet Management Solutions",
        description: "Real-time vehicle tracking and fleet management made easy. Boost efficiency and monitor performance with Drivi App.",
        image: "/og-image.jpg",
        url: "https://drivi.io",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Drivi - Smart Fleet Management Solutions",
        description: "Optimize fleet management with Drivi. Real-time vehicle tracking and performance monitoring.",
        image: "/twitter-image.jpg",
        site: "@DriviApp"
      }
    }
  },
  es: {
    // Header
    nav: {
      features: "Características",
      trust: "Confían en Nosotros",
      waitlist: "Lista de Espera",
      pricing: "Precios",
      caseStudies: "Casos de Éxito",
      support: "Soporte",
      maintenance: "Mantenimiento",
      alerts: "Alertas",
      contact: "Contacto",
      roiCalculator: "Calculadora ROI"
    },
    // Hero Section
    hero: {
      badge: "Solución de Gestión de Flotas Empresariales",
      title: "Gestión Inteligente de Flotas para Empresas Modernas",
      subtitle: "Seguimiento en tiempo real, optimización de mantenimiento y análisis de flota para empresas de todos los tamaños",
      cta: {
        demo: "Solicitar Demo",
        plans: "Ver Planes"
      },
      stats: {
        uptime: {
          value: "99.9%",
          label: "Disponibilidad"
        },
        clients: {
          value: "500+",
          label: "Clientes Activos"
        },
        vehicles: {
          value: "5,000+",
          label: "Vehículos Monitorizados"
        }
      },
      waitlistPlaceholder: "Introduce tu email",
      waitlistSubmit: "Unirse a la Lista",
      waitlistSubmitting: "Uniéndose...",
      waitlistSuccess: "¡Genial! Estás en la lista. Nos pondremos en contacto pronto.",
      waitlistError: "¡Ups! Algo salió mal. Por favor, inténtalo de nuevo."
    },
    // Features Section
    features: {
      title: "Todo lo que Necesitas para Gestionar tu Flota",
      subtitle: "Herramientas completas diseñadas para gestores de flotas y propietarios de empresas",
      items: [
        {
          title: "Análisis en Tiempo Real",
          description: "Monitoriza el rendimiento de tu flota con análisis detallados y paneles personalizables."
        },
        {
          title: "Mantenimiento Inteligente",
          description: "Alertas de mantenimiento predictivo y programación para prevenir averías."
        },
        {
          title: "Seguridad Mejorada",
          description: "Funciones avanzadas de seguridad incluyendo geovallas y alertas en tiempo real."
        },
        {
          title: "Optimización de Rutas",
          description: "Planificación de rutas con IA para reducir el consumo de combustible."
        },
        {
          title: "Gestión de Conductores",
          description: "Seguimiento del comportamiento y rendimiento del conductor."
        },
        {
          title: "Informes Automatizados",
          description: "Genera informes completos automáticamente para mejor toma de decisiones."
        }
      ]
    },
    // Trust Section
    trust: {
      title: "Empresas que Confían en Nosotros",
      subtitle: "Únete a las empresas que confían en Drivi para la gestión de sus flotas",
      partners: {
        cityExpress: {
          name: "Local Delivery Co.",
          type: "Entrega Local",
          vehicles: "12 vehículos",
          testimonial: "Drivi nos ayudó a optimizar nuestras rutas y reducir costes de combustible en un 25%."
        },
        greenLogistics: {
          name: "Eco Transport Solutions",
          type: "Transporte Regional",
          vehicles: "8 vehículos",
          testimonial: "Como empresa de transporte en crecimiento, necesitábamos una solución escalable. Drivi nos la proporcionó."
        },
        quickCourier: {
          name: "Swift Courier Services",
          type: "Mensajería Local",
          vehicles: "15 vehículos",
          testimonial: "El seguimiento en tiempo real ha transformado nuestras operaciones."
        },
        taxiFleet: {
          name: "City Ride Co.",
          type: "Servicio de Transporte",
          vehicles: "20 vehículos",
          testimonial: "Gestionar nuestra flota nunca había sido tan fácil."
        }
      },
      metrics: {
        activeFleets: {
          value: "25+",
          label: "Flotas Activas"
        },
        vehiclesTracked: {
          value: "200+",
          label: "Vehículos Monitorizados"
        },
        satisfaction: {
          value: "95%",
          label: "Satisfacción del Cliente"
        },
        costReduction: {
          value: "25%",
          label: "Reducción Media de Costes"
        }
      }
    },
    // Mobile App Section
    mobileApp: {
      badge: "Experiencia Móvil",
      title: "Controla tu Flota desde Cualquier Lugar",
      subtitle: "Accede a datos de flota en tiempo real, gestiona operaciones y responde a alertas al instante con nuestra potente aplicación móvil.",
      features: {
        crossPlatform: {
          title: "Multiplataforma",
          description: "Disponible para dispositivos iOS y Android"
        },
        secureAccess: {
          title: "Acceso Seguro",
          description: "Soporte de autenticación biométrica"
        },
        realTime: {
          title: "Actualizaciones en Tiempo Real",
          description: "Notificaciones y alertas instantáneas"
        },
        fullControl: {
          title: "Control Total",
          description: "Gestiona todas las operaciones de flota remotamente"
        }
      },
      cta: {
        ios: "Descargar para iOS",
        android: "Descargar para Android"
      }
    },
    // Case Studies Section
    caseStudies: {
      title: "Casos de Éxito",
      subtitle: "Descubre cómo las principales empresas transformaron sus operaciones de flota",
      cases: [
        {
          company: "Transportes García e Hijos",
          type: "Logística",
          description: "Empresa familiar que redujo costes de combustible en 30% y mejoró tiempos de entrega en 25% usando nuestra optimización de rutas."
        },
        {
          company: "Ambulancias Tenorio",
          type: "Sanidad",
          description: "Logró una utilización de flota del 99.9% y redujo los tiempos de respuesta en 45% en la región de Extremadura."
        },
        {
          company: "Taxis Unidos Valencia",
          type: "Transporte",
          description: "Mejoró la satisfacción del cliente en 40% y redujo los costes operativos en 35% en su flota de 200 vehículos."
        }
      ],
      readMore: "Leer historia completa"
    },
    // Support Section
    support: {
      title: "Contáctanos",
      subtitle: "¿Tienes preguntas? Estamos aquí para ayudar.",
      status: "Todos los sistemas operativos",
      cards: {
        phone: {
          title: "Soporte Telefónico",
          description: "Obtén asistencia inmediata de nuestro equipo",
          contact: "+34 676 984 400"
        },
        technical: {
          title: "Soporte Técnico",
          description: "Asistencia técnica experta para tu implementación",
          response: "< 2 horas de respuesta"
        },
        account: {
          title: "Gestión de Cuenta",
          description: "Gestor de cuenta dedicado para clientes enterprise",
          availability: "Disponible 24/7"
        }
      },
      form: {
        title: "Contacto",
        subtitle: "Rellena el formulario y nos pondremos en contacto contigo lo antes posible.",
        firstName: "Nombre",
        lastName: "Apellidos",
        email: "Email Profesional",
        company: "Empresa",
        message: "Mensaje",
        submit: "Enviar Mensaje",
        sending: "Enviando...",
        successMessage: "¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.",
        errorMessage: "Error al enviar el mensaje. Por favor, inténtalo de nuevo.",
        firstNamePlaceholder: "Introduce tu nombre",
        lastNamePlaceholder: "Introduce tus apellidos",
        emailPlaceholder: "Introduce tu email profesional",
        companyPlaceholder: "Introduce el nombre de tu empresa",
        messagePlaceholder: "¿En qué podemos ayudarte?"
      }
    },
    // ROI Section
    roi: {
      title: "Calculadora ROI",
      subtitle: "Calcula tu ahorro potencial con Drivi",
      form: {
        fleetSize: "Tamaño de la Flota",
        mileage: "Kilometraje Mensual Promedio por Vehículo",
        fuelCost: "Coste de Combustible",
        maintenanceCost: "Coste Mensual de Mantenimiento por Vehículo"
      },
      results: {
        title: "Tu Ahorro Anual Potencial",
        fuelSavings: {
          label: "Ahorro en Combustible"
        },
        maintenanceSavings: {
          label: "Ahorro en Mantenimiento"
        },
        efficiencySavings: {
          label: "Ganancias en Eficiencia"
        },
        totalSavings: {
          label: "Ahorro Anual Total"
        }
      },
      cta: {
        calculate: "Descargar Informe Completo"
      }
    },
    // CTA Section
    cta: {
      title: "¿Listo para Transformar tus Operaciones de Flota?",
      subtitle: "Únete a los líderes de la industria que confían en Drivi para su gestión de flotas",
      demo: "Programar Demo Empresarial",
      contact: "Contactar Ventas"
    },
    // Footer
    footer: {
      tagline: "Transforma tu gestión de flotas con Drivi",
      sections: {
        product: {
          title: "Producto",
          links: ["Características", "Precios", "Actualizaciones", "Programa Beta"]
        },
        company: {
          title: "Empresa",
          links: ["Sobre Nosotros", "Empleo", "Blog", "Prensa"]
        },
        legal: {
          title: "Legal",
          links: ["Privacidad", "Términos", "Seguridad", "RGPD"]
        }
      },
      copyright: "© 2024 Drivi. Todos los derechos reservados."
    },
    // Fleet Status Section
    fleetStatus: {
      title: "Estado de Flota en Vivo",
      types: {
        delivery: "Entrega",
        transport: "Transporte",
        emergency: "Emergencia",
        service: "Servicio"
      },
      alerts: {
        stopped: "Vehículo detenido para entrega",
        delay: "Retraso por tráfico reportado",
        completed: "Entrega completada",
        deviation: "Desviación de ruta detectada"
      },
      total: "Total",
      active: "activos",
      stopped: "detenidos",
      utilization: "utilización",
      totalActive: "Total Activos",
      fleetSize: "Tamaño de Flota",
      recentAlerts: "Alertas Recientes"
    },
    // Pricing Section
    pricing: {
      title: "Precios simples y transparentes",
      subtitle: "Elige el plan perfecto para tus necesidades de gestión de flotas",
      plans: {
        basic: {
          name: "Básico",
          price: "$Próximo",
          period: "por mes",
          description: "Perfecto para flotas pequeñas y startups",
          features: [
            "Hasta 10 vehículos",
            "Seguimiento en tiempo real",
            "Análisis básico",
            "Soporte por email",
            "Optimización de rutas",
            "Análisis avanzado",
            "Acceso API",
            "Integraciones personalizadas"
          ],
          cta: "Comenzar Básico"
        },
        professional: {
          name: "Profesional",
          price: "$Próximo",
          period: "por mes",
          description: "Ideal para flotas en crecimiento",
          features: [
            "Hasta 50 vehículos",
            "Seguimiento en tiempo real",
            "Análisis básico",
            "Soporte prioritario",
            "Optimización de rutas",
            "Análisis avanzado",
            "Acceso API",
            "Integraciones personalizadas"
          ],
          cta: "Comenzar Profesional",
          popular: "Más Popular"
        },
        enterprise: {
          name: "Empresarial",
          price: "Personalizado",
          period: "contáctanos",
          description: "Para flotas grandes y requisitos especiales",
          features: [
            "Vehículos ilimitados",
            "Seguimiento en tiempo real",
            "Análisis básico",
            "Soporte telefónico 24/7",
            "Optimización de rutas",
            "Análisis avanzado",
            "Acceso API",
            "Integraciones personalizadas"
          ],
          cta: "Contactar Ventas"
        }
      }
    },
    comingSoon: {
      backToHome: "Volver al Inicio",
      title: "Próximamente",
      description: "Estamos trabajando para traerte la mejor plataforma de gestión de flotas.",
      waitlistTitle: "Únete a la Lista de Espera",
      waitlistDescription: "Sé el primero en saber cuando lancemos.",
      features: {
        speed: {
          title: "Ultra Rápido",
          description: "Actualizaciones en tiempo real y notificaciones instantáneas"
        },
        security: {
          title: "Seguridad Bancaria",
          description: "Tus datos están protegidos con encriptación empresarial"
        },
        analytics: {
          title: "Análisis Inteligente",
          description: "Toma decisiones basadas en datos con análisis avanzados"
        }
      },
      waitlist: {
        benefits: {
          earlyAccess: {
            title: "Acceso Anticipado",
            description: "Sé de los primeros en probar nuestra plataforma"
          },
          exclusiveUpdates: {
            title: "Actualizaciones Exclusivas",
            description: "Recibe noticias y actualizaciones privilegiadas"
          },
          specialOffers: {
            title: "Ofertas Especiales",
            description: "Acceso a descuentos de lanzamiento y promociones"
          }
        },
        subtitle: "Únete a la lista de espera hoy"
      }
    },
    seo: {
      title: "Drivi - Soluciones de Gestión de Flotas y Seguimiento de Vehículos",
      description: "Optimiza la gestión de tu flota con Drivi. Seguimiento en tiempo real, optimización de rutas y monitoreo de rendimiento para mejorar la eficiencia y seguridad.",
      keywords: "seguimiento de flotas, seguimiento de vehículos, gestión de flotas, seguimiento en tiempo real, monitoreo GPS, seguridad vehicular, eficiencia de flotas, optimización de rutas",
      og: {
        title: "Drivi - Soluciones Inteligentes de Gestión de Flotas",
        description: "Seguimiento de vehículos y gestión de flotas en tiempo real. Mejora la eficiencia y monitorea el rendimiento con la App Drivi.",
        image: "/og-image.jpg",
        url: "https://drivi.io",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Drivi - Soluciones Inteligentes de Gestión de Flotas",
        description: "Optimiza la gestión de flotas con Drivi. Seguimiento de vehículos y monitoreo de rendimiento en tiempo real.",
        image: "/twitter-image.jpg",
        site: "@DriviApp"
      }
    }
  }
}; 